import type { ProductRow } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { Note } from "../../components/Note"
import type { Column } from "./columns"
import { previewSiteName, valueOf } from "./columns"

/**
 * Панель важных атрибутов с пояснением, зачем они нужны.
 *
 * Тексты не выдуманы: короткое имя товара собирается в `ProductNameGenerator.cs:44-95`
 * ровно так — название группы, значения важных атрибутов по приоритету, торговая
 * марка в скобках, артикул. Это имя уезжает на сайт названием товара
 * (`SitePartialRefresher.cs:67`). Главный атрибут всегда важный номер один и
 * сдвинуть его нельзя (`GroupWriter.cs:183-197`, `GroupAttributeManager.cs:196-197`).
 */
export default function ImportancePanel({
	columns,
	rows,
	groupName,
	hasSellmark,
	rights,
	busy,
	onMove,
	onSetMain,
}: {
	columns: Column[]
	rows: ProductRow[]
	groupName: string
	hasSellmark: boolean
	rights: GroupRights
	busy: boolean
	onMove: (column: Column, step: number) => void
	onSetMain: (column: Column) => void
}) {
	// пример строим по первому товару карточки — так виднее, что получится
	const sample = rows[0]
	const example = sample
		? previewSiteName(groupName || "Название группы", columns, sample, hasSellmark)
		: null

	// товар без значения хотя бы в одном важном атрибуте имя не получит вовсе
	const incomplete = rows.filter(row =>
		columns.some(column => valueOf(row, column.id).trim() === "")
	).length

	return (
		<div className="panel tp-columns">
			<div className="panel__head">Важные атрибуты</div>
			<div className="panel__body">
				<p className="tp-help">
					Из важных атрибутов ERP собирает <b>название товара для сайта</b>:
					название группы → значения важных атрибутов в том порядке, что задан
					здесь → торговая марка → артикул. Порядок колонок в таблице на имя
					не влияет, влияет только этот список.
				</p>

				<p className="tp-help tp-help--example">
					Как это выглядит в живой карточке{" "}
					<span className="mono">344371</span>: важный атрибут там один —
					«Размер» со значением «19-83» и единицей «мм», и на сайт товар уходит
					с именем
					<br />
					<b>
						Труборез для глушителя, o19-83 мм, MAGMA MG50761 Размер 19-83 мм
						344371
					</b>
				</p>

				{example && sample && (
					<p className="tp-help">
						В этой карточке у товара <span className="mono">{sample.id}</span>{" "}
						имя собралось бы примерно так:
						<br />
						<b>{example}</b>
						<br />
						<span className="muted">
							Единицы измерения ERP подставит сама, поэтому в готовом имени
							будет, например, «Длина 150 мм».
						</span>
					</p>
				)}

				{incomplete > 0 && (
					<Note tone="hint">
						У {incomplete} товаров карточки заполнены не все важные атрибуты.
						Для таких товаров имя не соберётся — ERP пропускает товар целиком,
						если хоть одно важное значение пустое.
					</Note>
				)}

				<div className="tp-colrow__head">Порядок</div>

				{columns.map((column, index) => (
					<div className="tp-colrow" key={column.id}>
						<span
							className={"tp-rank" + (column.isMain ? " tp-rank--main" : "")}
						>
							{column.isMain ? "★" : column.importantSort}
						</span>
						<span className="tp-colrow__name" title={column.name}>
							{column.name}
							{column.isMain && (
								<span className="muted"> — главный атрибут</span>
							)}
						</span>
						<button
							className="tp-mini"
							title="Выше в списке важных"
							disabled={
								!rights.important ||
								busy ||
								column.isMain ||
								(column.importantSort ?? 0) <= 2
							}
							onClick={() => onMove(column, -1)}
						>
							↑
						</button>
						<button
							className="tp-mini"
							title="Ниже в списке важных"
							disabled={
								!rights.important ||
								busy ||
								column.isMain ||
								index === columns.length - 1
							}
							onClick={() => onMove(column, 1)}
						>
							↓
						</button>
						<button
							className="tp-mini"
							title="Поставить этот атрибут первым в названии товара"
							disabled={!rights.mainAttribute || column.isMain || busy}
							onClick={() => onSetMain(column)}
						>
							сделать главным
						</button>
					</div>
				))}

				<p className="tp-help tp-help--small">
					<b>Главный атрибут</b> — тот, которым товары карточки отличаются друг
					от друга в первую очередь: у ключей это размер, у удлинителей длина,
					у ламп мощность. Он всегда идёт первым в названии и всегда важный —
					снять с него звёздочку или подвинуть в списке ERP не даёт. У наборов
					он вдобавок уезжает на сайт: по нему сайт показывает состав набора.
					<br />
					Признак «важный» ставится звёздочкой в заголовке колонки — там же
					видно место атрибута в этом списке.
				</p>
			</div>
		</div>
	)
}
